import { createSlice } from "@reduxjs/toolkit";

import { ProfileState, RootState } from "@/types/store";
import {
  updateInfo,
  updateProfilePicture,
  updateEmergencyContact,
  removeProfilePicture,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/profile/thunks";
import { SystemNotification } from "@/types/user";

const initialState: ProfileState = {
  infoLoading: false,
  removeProfilePictureLoading: false,
  emergencyContactLoading: false,
  infoError: null,
  removeProfilePictureError: null,
  emergencyContactError: null,
  notifications: [],
  totalNotifications: 0,
  isUnreadNotifications: false,
};

const ProfileSlice = createSlice({
  name: "ProfileSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateInfo.pending, (state) => {
        state.infoLoading = true;
      })
      .addCase(updateInfo.fulfilled, (state) => {
        state.infoLoading = false;
      })
      .addCase(updateInfo.rejected, (state, action) => {
        state.infoLoading = false;
        state.infoError = action.payload as string;
      })
      .addCase(removeProfilePicture.pending, (state) => {
        state.removeProfilePictureLoading = true;
      })
      .addCase(removeProfilePicture.fulfilled, (state) => {
        state.removeProfilePictureLoading = false;
      })
      .addCase(removeProfilePicture.rejected, (state, action) => {
        state.removeProfilePictureLoading = false;
        state.removeProfilePictureError = action.payload as string;
      })
      .addCase(updateEmergencyContact.pending, (state) => {
        state.emergencyContactLoading = true;
      })
      .addCase(updateEmergencyContact.fulfilled, (state) => {
        state.emergencyContactLoading = false;
      })
      .addCase(updateEmergencyContact.rejected, (state, action) => {
        state.emergencyContactLoading = false;
        state.emergencyContactError = action.payload as string;
      })
      .addCase(updateProfilePicture.pending, (state) => {
        state.infoLoading = true;
      })
      .addCase(updateProfilePicture.fulfilled, (state) => {
        state.infoLoading = false;
      })
      .addCase(updateProfilePicture.rejected, (state, action) => {
        state.infoLoading = false;
        state.infoError = action.payload as string;
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.notifications = action.meta.arg.isInitial
          ? [...action.payload.notifications]
          : [...state.notifications, ...action.payload.notifications];
        state.totalNotifications = action.payload.total;
        state.isUnreadNotifications = action.payload.unreadNotifications;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map(
          (item: SystemNotification) => ({ ...item, read: true })
        );
        state.isUnreadNotifications = false;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        state.notifications = state.notifications.map(
          (item: SystemNotification) =>
            item.id === action.payload ? { ...item, read: true } : item
        );

        // Check if there are any unread notifications left after marking this one as read
        const hasUnreadNotifications = state.notifications.some(
          (notification: SystemNotification) => !notification.read
        );

        state.isUnreadNotifications = hasUnreadNotifications;
      });
  },
});

export const selectInfoLoading = (state: RootState) =>
  state.Profile.infoLoading;
export const selectRemoveProfilePictureLoading = (state: RootState) =>
  state.Profile.removeProfilePictureLoading;
export const selectEmergencyContactLoading = (state: RootState) =>
  state.Profile.emergencyContactLoading;
export const selectInfoError = (state: RootState) => state.Profile.infoError;
export const selectEmergencyContactError = (state: RootState) =>
  state.Profile.emergencyContactError;
export const selectNotifications = (state: RootState) =>
  state.Profile.notifications;
export const selectIsUnreadNotifications = (state: RootState) =>
  state.Profile.isUnreadNotifications;
export const selectTotalNotifications = (state: RootState) =>
  state.Profile.totalNotifications;

export default ProfileSlice.reducer;
